import { Text } from '@nike/eds'
import { TrinityButton } from 'components/trinity-button'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type GradeReasonProperties } from 'types'

interface GradeButtonsProps {
  gradeReason: GradeReasonProperties
  setGradeReason: (gradeReason: GradeReasonProperties) => void
  reasons: GradeReasonProperties[]
  gradeText: string
  hasInstruction?: boolean
  rasnChannel: string
}

// Check if a reason is a DENY reason (refundColorsOn: false indicates a DENY reason)
const isDenyReason = (gp: GradeReasonProperties): boolean => {
  return gp.refundColorsOn === false
}

export const GradeReasonButtons = ({ gradeReason, setGradeReason, reasons, gradeText, hasInstruction, rasnChannel }: GradeButtonsProps) => {
  const { t } = useTranslation()

  const renderButton = (gp: GradeReasonProperties, className: string) => {
    const isActive = gradeReason.grade === gp.grade.toUpperCase()
    const isDeny = isDenyReason(gp)
    const color = isDeny
      ? 'background-red' // DENY reasons are always red
      : gp.refundColorsOn
        ? gp.refundApprovalRasnChannels.includes(rasnChannel ?? '')
          ? 'background-green'
          : 'background-red'
        : ''
    return (
      <TrinityButton key={gp.reasonCode} className={`${className} normal-case ${color}`} active={isActive} onClick={() => { setGradeReason(gp) }}>
        {gp.reason}
      </TrinityButton>
    )
  }

  const gradeButtonsWithInstructions = useMemo(() => {
    if (!hasInstruction) return []
    return reasons.filter(r => r.isSpecialInstruction && !isDenyReason(r))
      .map(gp => renderButton(gp, 'bg-amber-200'))
  }, [reasons, gradeReason.grade, setGradeReason])

  // Regular approve buttons (exclude DENY reasons)
  const gradeButtons = useMemo(() => {
    return reasons.filter(r => !r.isSpecialInstruction && !isDenyReason(r))
      .map(gp => renderButton(gp, 'px-24'))
  }, [reasons, gradeReason.grade, setGradeReason])

  // DENY buttons rendered separately at the bottom with full width
  const denyButtons = useMemo(() => {
    return reasons.filter(r => isDenyReason(r))
      .map(gp => renderButton(gp, 'w-full'))
  }, [reasons, gradeReason.grade, setGradeReason])

  return <>
    {hasInstruction && gradeButtonsWithInstructions.length > 0 &&
      <>
        <Text font={'display-2'} as={'h1'} className={'mb-6'}>
          {t('grading.special_instruction')}
        </Text>
        <div className="grade-button-instruction-group-container mb-10">
          {gradeButtonsWithInstructions}
        </div>
      </>
    }
    <Text font={'display-2'} as={'h1'} className={'mb-6'}>
      {gradeText} {t('grading.reason')}
    </Text>
    <div className={`grade-button-group-container h-${hasInstruction ? '60' : '100'}`}>
      {gradeButtons}
    </div>
    {denyButtons.length > 0 && (
      <div className="deny-button-container mt-6 w-full">
        {denyButtons}
      </div>
    )}
  </>
}
